/**
 * SSO providers for the current tenant.
 *
 * Comes from Open edX's own third_party_auth configuration — `OAuth2ProviderConfig`
 * rows scoped to the tenant's Site — so which buttons a learner sees is a database
 * fact per academy, not a build-time list.
 */

import { openedx } from './openedx';

export interface AuthProvider {
  id: string;
  name: string;
  iconClass: string | null;
  iconImage: string | null;
  loginUrl: string;
  registerUrl: string;
  skipRegistrationForm: boolean;
}

export interface AuthContext {
  currentProvider: string | null;
  providers: AuthProvider[];
  secondaryProviders: AuthProvider[];
  finishAuthUrl: string | null;
  errorMessage: string | null;
}

export const EMPTY_AUTH_CONTEXT: AuthContext = {
  currentProvider: null,
  providers: [],
  secondaryProviders: [],
  finishAuthUrl: null,
  errorMessage: null,
};

export async function loadAuthContext(next = '/'): Promise<AuthContext> {
  // Our hostname is sent explicitly. The request goes TO the LMS, so its Host header is
  // the LMS's — the server cannot work out which academy is asking without being told.
  const host = window.location.hostname;

  // Never fatal: a tenant with no SSO configured still needs a working password form,
  // so a failure here degrades to "no provider buttons" rather than a broken page.
  try {
    return await openedx.get<AuthContext>(
      `/api/tenant/v1/auth-providers?host=${encodeURIComponent(host)}&next=${encodeURIComponent(next)}`,
    );
  } catch {
    return EMPTY_AUTH_CONTEXT;
  }
}

/**
 * Provider login/register URLs are LMS-relative paths from the pipeline, so they need
 * the LMS origin prepended before the browser can follow them.
 */
export function absolute(url: string, lmsOrigin: string): string {
  return url.startsWith('http') ? url : `${lmsOrigin}${url}`;
}
